// /server — instance operations (admin only): stats dashboard, storage
// maintenance (GC / compaction), and per-vault attachment quotas.

import { useState } from "react";
import { toast } from "sonner";
import { IconRefresh, IconTrash } from "@tabler/icons-react";
import {
  errorText,
  listServerVaults,
  runCompaction,
  runGc,
  serverStats,
  setVaultQuota,
  type ServerVault,
} from "../api";
import { useApi } from "../session";
import { useAsyncData } from "../lib/use-async";
import { fmtBytes } from "../lib/format";
import { RequireRole } from "../app/RequireRole";
import { PageHeader } from "@/components/page-header";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { CopyId } from "@/components/copy-id";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export function ServerPage() {
  return (
    <RequireRole min="admin">
      <ServerInner />
    </RequireRole>
  );
}

function ServerInner() {
  const api = useApi();
  const stats = useAsyncData(() => serverStats(api));
  const vaults = useAsyncData(() => listServerVaults(api));

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader eyebrow="Instance operations" title="Server" />

      {stats.error && (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load stats: {stats.error}</AlertDescription>
        </Alert>
      )}
      {stats.data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Stat label="Users" value={String(stats.data.users)} />
          <Stat label="Vaults" value={String(stats.data.vaults)} />
          <Stat label="Documents" value={String(stats.data.docs)} sub={fmtBytes(stats.data.doc_bytes)} />
          <Stat
            label="Attachments"
            value={String(stats.data.attachments)}
            sub={fmtBytes(stats.data.attachment_bytes)}
          />
          <Stat label="Active sessions" value={String(stats.data.active_sessions)} />
        </div>
      )}

      <MaintenanceCard onDone={() => { stats.reload(); vaults.reload(); }} />

      <QuotasCard vaults={vaults.data} error={vaults.error} loading={vaults.loading} onChanged={vaults.reload} />
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1">
        <span className="font-mono text-xs tracking-wider text-muted-foreground uppercase">
          {label}
        </span>
        <span className="text-2xl font-semibold tabular-nums">{value}</span>
        {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
      </CardContent>
    </Card>
  );
}

function MaintenanceCard({ onDone }: { onDone: () => void }) {
  const api = useApi();

  async function gc() {
    try {
      const r = await runGc(api);
      toast.success("Garbage collection complete", {
        description: `${r.docs_deleted} docs, ${r.attachments_deleted} attachments, ${fmtBytes(r.bytes_reclaimed)} reclaimed`,
      });
      onDone();
    } catch (e) {
      toast.error("GC failed", { description: errorText(e) });
    }
  }

  async function compact() {
    try {
      const r = await runCompaction(api);
      toast.success("Compaction complete", {
        description: `${r.docs_compacted} docs, ${fmtBytes(r.bytes_before)} → ${fmtBytes(r.bytes_after)}`,
      });
      onDone();
    } catch (e) {
      toast.error("Compaction failed", { description: errorText(e) });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Storage maintenance</CardTitle>
        <CardDescription>
          Reclaim space from deleted content and compact document history. Safe to run anytime.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-3">
        <ConfirmDialog
          trigger={
            <Button variant="outline">
              <IconTrash data-icon="inline-start" />
              Run garbage collection
            </Button>
          }
          title="Run garbage collection?"
          description="Permanently removes soft-deleted docs and unreferenced attachments past their grace period."
          confirmLabel="Run GC"
          onConfirm={gc}
        />
        <ConfirmDialog
          trigger={
            <Button variant="outline">
              <IconRefresh data-icon="inline-start" />
              Run compaction
            </Button>
          }
          title="Run compaction?"
          description="Compacts document history to reduce storage. This can take a while on large instances."
          confirmLabel="Run compaction"
          onConfirm={compact}
        />
      </CardContent>
    </Card>
  );
}

function QuotasCard({
  vaults,
  error,
  loading,
  onChanged,
}: {
  vaults: ServerVault[] | null;
  error: string | null;
  loading: boolean;
  onChanged: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Vaults & quotas</CardTitle>
        <CardDescription>Every vault on this instance, its usage, and its attachment quota.</CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {loading && !vaults && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Spinner className="size-4" /> <span className="font-mono text-xs">loading…</span>
          </div>
        )}
        {vaults && (
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vault</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Attachments</TableHead>
                  <TableHead>Quota</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vaults.map((v) => (
                  <TableRow key={v.vault.id}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        {v.vault.name}
                        {v.deleted && <Badge variant="destructive">deleted</Badge>}
                      </span>
                    </TableCell>
                    <TableCell>
                      <CopyId id={v.vault.id} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{v.member_count}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtBytes(v.attachment_bytes)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {v.vault.attachment_quota_bytes !== undefined
                        ? fmtBytes(v.vault.attachment_quota_bytes)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <QuotaDialog vault={v} onChanged={onChanged} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QuotaDialog({ vault, onChanged }: { vault: ServerVault; onChanged: () => void }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const currentMiB = vault.vault.attachment_quota_bytes
    ? Math.round(vault.vault.attachment_quota_bytes / (1024 * 1024))
    : 0;
  const [mib, setMib] = useState(String(currentMiB));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const n = Number(mib);
    if (!Number.isFinite(n) || n < 0) {
      setError("Enter a non-negative number of MiB.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setVaultQuota(api, vault.vault.id, Math.round(n * 1024 * 1024));
      toast.success(`Quota updated for ${vault.vault.name}`);
      setOpen(false);
      onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        setMib(String(currentMiB));
        setError(null);
      }}
    >
      <Button variant="ghost" size="sm" render={<DialogTrigger />}>
        Set quota
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attachment quota — {vault.vault.name}</DialogTitle>
          <DialogDescription>The maximum total attachment storage for this vault.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="quota-mib">Quota (MiB)</FieldLabel>
              <Input
                id="quota-mib"
                inputMode="numeric"
                value={mib}
                onChange={(e) => setMib(e.target.value)}
                autoFocus
              />
              <FieldDescription>0 disables uploads. Currently using {fmtBytes(vault.attachment_bytes)}.</FieldDescription>
            </Field>
          </FieldGroup>
        </form>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button disabled={busy} onClick={() => void save()}>
            {busy && <Spinner />}
            Save quota
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
